/*! © 2026 AirX (airx.art). All rights reserved. Unauthorized copying, reproduction, or redistribution of this code is prohibited. Report infringement: info@airx.art */

(function () {
  'use strict';

  const html = document.documentElement;
  const motionOn = () => !!window.gsap && html.classList.contains('motion');

  class HeaderNav extends HTMLElement {
    connectedCallback() {
      this.header = this.querySelector('.header');
      if (!this.header) return;
      this.hideOnScroll = this.dataset.hideOnScroll === 'true';
      this.lastY = window.scrollY;
      this.openItem = null;
      this.ticking = false;

      this.boundScroll = () => {
        if (this.ticking) return;
        this.ticking = true;
        window.requestAnimationFrame(() => {
          this.onScroll();
          this.ticking = false;
        });
      };
      window.addEventListener('scroll', this.boundScroll, { passive: true });
      this.onScroll();

      this.initNavItems();

      const hamburger = this.querySelector('.header__hamburger');
      if (hamburger && window.AirX && window.AirX.on) {
        window.AirX.on('drawer:open', (e) => {
          if (e.detail && e.detail.id === 'MenuDrawer') hamburger.setAttribute('aria-expanded', 'true');
        });
        window.AirX.on('drawer:close', (e) => {
          if (e.detail && e.detail.id === 'MenuDrawer') hamburger.setAttribute('aria-expanded', 'false');
        });
      }

      document.addEventListener('click', (e) => {
        if (this.openItem && !this.contains(e.target)) this.closePanel();
      });
      this.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape' || !this.openItem) return;
        const trigger = this.openItem.querySelector('[data-nav-trigger]');
        this.closePanel();
        if (trigger) trigger.focus();
      });
      this.addEventListener('focusout', (e) => {
        if (this.openItem && e.relatedTarget && !this.contains(e.relatedTarget)) this.closePanel();
      });
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.boundScroll);
    }

    onScroll() {
      const y = window.scrollY;
      this.header.classList.toggle('is-scrolled', y > 80);

      if (this.hideOnScroll && html.classList.contains('motion')) {
        const delta = y - this.lastY;
        if (y < 160) {
          this.header.classList.remove('is-hidden');
        } else if (delta > 8 && !this.openItem) {
          this.header.classList.add('is-hidden');
        } else if (delta < -8) {
          this.header.classList.remove('is-hidden');
        }
      }
      this.lastY = y;
    }

    initNavItems() {
      const fine = window.matchMedia('(pointer: fine)').matches;

      this.querySelectorAll('[data-nav-item]').forEach((item) => {
        const trigger = item.querySelector('[data-nav-trigger]');
        if (!trigger) return;
        const panel = document.getElementById(trigger.getAttribute('aria-controls'));
        if (!panel) return;

        let openTimer;
        let closeTimer;
        const scheduleClose = () => {
          window.clearTimeout(openTimer);
          closeTimer = window.setTimeout(() => {
            if (this.openItem === item) this.closePanel();
          }, 180);
        };

        if (fine) {
          item.addEventListener('pointerenter', (e) => {
            if (e.pointerType !== 'mouse') return;
            window.clearTimeout(closeTimer);
            openTimer = window.setTimeout(() => this.openPanel(item, trigger, panel), 70);
          });
          item.addEventListener('pointerleave', (e) => {
            if (e.pointerType !== 'mouse') return;
            scheduleClose();
          });

          panel.addEventListener('pointerenter', (e) => {
            if (e.pointerType !== 'mouse') return;
            window.clearTimeout(closeTimer);
          });
          panel.addEventListener('pointerleave', (e) => {
            if (e.pointerType !== 'mouse') return;
            scheduleClose();
          });
        }

        trigger.addEventListener('click', (e) => {
          if (trigger.getAttribute('aria-expanded') !== 'true') {
            e.preventDefault();
            this.openPanel(item, trigger, panel);
          }
        });
      });
    }

    openPanel(item, trigger, panel) {
      if (this.openItem === item) return;
      this.closePanel(true);
      this.openItem = item;
      trigger.setAttribute('aria-expanded', 'true');
      panel.hidden = false;
      panel.classList.add('is-open');
      this.header.classList.add('is-panel-open');

      if (motionOn()) {
        const gsap = window.gsap;
        gsap.fromTo(
          panel,
          { opacity: 0, y: -14 },
          { opacity: 1, y: 0, duration: 0.45, ease: 'power3.out', clearProps: 'transform' }
        );
        const items = panel.querySelectorAll('[data-mega-item]');
        if (items.length) {
          gsap.fromTo(
            items,
            { opacity: 0, y: 18 },
            { opacity: 1, y: 0, duration: 0.55, ease: 'power3.out', stagger: 0.05, delay: 0.06, clearProps: 'all' }
          );
        }
      }
    }

    closePanel(keepHeaderState) {
      if (!this.openItem) return;
      const item = this.openItem;
      this.openItem = null;
      const trigger = item.querySelector('[data-nav-trigger]');
      if (trigger) {
        trigger.setAttribute('aria-expanded', 'false');
        const panel = document.getElementById(trigger.getAttribute('aria-controls'));
        if (panel) {
          panel.classList.remove('is-open');
          panel.hidden = true;
        }
      }
      if (!keepHeaderState) this.header.classList.remove('is-panel-open');
    }
  }

  if (!customElements.get('header-nav')) customElements.define('header-nav', HeaderNav);

  if (window.AirX && window.AirX.DrawerElement && !customElements.get('menu-drawer')) {
    customElements.define('menu-drawer', class MenuDrawer extends window.AirX.DrawerElement {});
  }
})();
